import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { generateScript, ScriptGenerationRequest, ScriptResponse } from '@/api';

interface Props {
  open: boolean;
  onClose: () => void;
  onScriptGenerated: (script: ScriptResponse) => void;
  projectId?: string;
}

export default function ScriptGenerationModal({ open, onClose, onScriptGenerated, projectId }: Props) {
  const [formData, setFormData] = useState<ScriptGenerationRequest>({
    topic: '',
    duration: 5,
    audience: '',
    style: ''
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.topic.trim()) {
      setError('Please enter a topic for your video');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const response = await generateScript(formData, projectId);
      onScriptGenerated(response);
      onClose();
      
      // Reset form
      setFormData({
        topic: '',
        duration: 5,
        audience: '',
        style: ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate script');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleChange = (field: keyof ScriptGenerationRequest, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            Generate AI Script
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Topic Input */}
          <div>
            <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-2">
              Video Topic <span className="text-red-500">*</span>
            </label>
            <textarea
              id="topic"
              value={formData.topic}
              onChange={(e) => handleChange('topic', e.target.value)}
              placeholder="E.g., How to automate your social media with AI in 2024"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              rows={3}
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Be specific! The AI will use this to determine your audience and create targeted content.
            </p>
          </div>

          {/* Duration Input */}
          <div>
            <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-2">
              Target Duration (minutes)
            </label>
            <select
              id="duration"
              value={formData.duration}
              onChange={(e) => handleChange('duration', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value={1}>1 minute (Short)</option>
              <option value={3}>3 minutes (Quick)</option>
              <option value={5}>5 minutes (Standard)</option>
              <option value={8}>8 minutes (Detailed)</option>
              <option value={10}>10 minutes (Comprehensive)</option>
              <option value={15}>15+ minutes (In-depth)</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Audience Input */}
            <div>
              <label htmlFor="audience" className="block text-sm font-medium text-gray-700 mb-2">
                Target Audience <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                id="audience"
                type="text"
                value={formData.audience}
                onChange={(e) => handleChange('audience', e.target.value)}
                placeholder="E.g., content creators, small business owners"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">Leave blank to let AI determine audience</p>
            </div>

            {/* Style Input */}
            <div>
              <label htmlFor="style" className="block text-sm font-medium text-gray-700 mb-2">
                Style & Tone <span className="text-gray-400">(Optional)</span>
              </label>
              <select
                id="style"
                value={formData.style}
                onChange={(e) => handleChange('style', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Auto-detect from topic</option>
                <option value="casual and friendly">Casual & Friendly</option>
                <option value="professional and authoritative">Professional & Authoritative</option>
                <option value="educational and clear">Educational & Clear</option>
                <option value="energetic and motivational">Energetic & Motivational</option>
                <option value="conversational and relatable">Conversational & Relatable</option>
              </select>
            </div>
          </div>

          {/* AI Features Preview */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
            <h4 className="font-medium text-indigo-900 mb-2">✨ AI will create:</h4>
            <ul className="text-sm text-indigo-700 space-y-1">
              <li>• Professional script with retention-optimized structure</li>
              <li>• Visual cues and timing directions for editing</li>
              <li>• SEO-optimized title, description, and tags</li>
              <li>• Production notes with asset requirements</li>
              <li>• Retention score with improvement suggestions</li>
            </ul>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isGenerating}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isGenerating || !formData.topic.trim()}
              className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Generate Script
                </>
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}